import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "../api/axiosInstance";

/**
 * Pilcore PMS
 * Market Intelligence Scope (Read-only)
 * + Competitor Room Types Mapping (by Room Category)
 */

const EMPTY_EVENT_FORM = {
  city_code: "",
  name: "",
  start_date: "",
  end_date: "",
  impact_level: "medium",
  expected_attendance: "",
  source: "",
  confidence: "",
  notes: "",
};

function formatEventDateInput(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function buildEventFormFromRow(row) {
  return {
    city_code: String(row?.city_code || "").trim(),
    name: String(row?.name || "").trim(),
    start_date: formatEventDateInput(row?.start_date),
    end_date: formatEventDateInput(row?.end_date),
    impact_level: String(row?.impact_level || "medium").trim() || "medium",
    expected_attendance: row?.expected_attendance ?? "",
    source: String(row?.source || "").trim(),
    confidence: row?.confidence ?? "",
    notes: String(row?.notes || "").trim(),
  };
}

function CityEventModal({ isOpen, isSaving, isEditing, form, setForm, onClose, onSave }) {
  const [contentReady, setContentReady] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    console.log("[CITY_EVENTS][MODAL] isOpen=true -> preparing content");
    setContentReady(false);

    async function loadEverything() {
      // No remote loads needed for this modal right now.
      return;
    }

    async function load() {
      await loadEverything();
      setContentReady(true);
      console.log("[CITY_EVENTS][MODAL] contentReady=true");
    }

    load();
  }, [isOpen]);

  if (!isOpen) return null;
  if (!contentReady) return null;

  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      role="dialog"
      style={{ background: "rgba(0,0,0,0.65)" }}
    >
      <div className="modal-dialog modal-lg" role="document" style={{ maxWidth: "900px" }}>
        <div
          className="modal-content bg-dark text-light"
          style={{ border: "1px solid rgba(255,255,255,0.10)" }}
        >
          <div className="modal-header d-flex align-items-start">
            <div className="pe-3">
              <h5 className="modal-title mb-0">{isEditing ? "Edit city event" : "Add city event"}</h5>
              <div className="text-white-50" style={{ fontSize: "0.85rem" }}>
                {isEditing
                  ? "Update the stored event data used later for pricing context."
                  : "This will be stored in DB and used later for pricing context."}
              </div>
            </div>

            <button
              type="button"
              className="btn btn-sm btn-outline-light ms-auto"
              onClick={onClose}
              disabled={isSaving}
            >
              Close
            </button>
          </div>

          <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
            <div className="row g-2">
              <div className="col-12 col-md-4">
                <label className="form-label text-white-50 mb-1">City code</label>
                <input
                  className="form-control form-control-sm"
                  value={form.city_code}
                  onChange={(e) => setForm((p) => ({ ...p, city_code: e.target.value }))}
                  disabled={isSaving}
                  placeholder="e.g. BCN"
                  autoFocus
                />
              </div>

              <div className="col-12 col-md-8">
                <label className="form-label text-white-50 mb-1">Event name</label>
                <input
                  className="form-control form-control-sm"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  disabled={isSaving}
                  placeholder="e.g. Mobile World Congress"
                />
              </div>

              <div className="col-12 col-md-4">
                <label className="form-label text-white-50 mb-1">Start date</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={form.start_date}
                  onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))}
                  disabled={isSaving}
                />
              </div>

              <div className="col-12 col-md-4">
                <label className="form-label text-white-50 mb-1">End date</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={form.end_date}
                  onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))}
                  disabled={isSaving}
                />
              </div>

              <div className="col-12 col-md-4">
                <label className="form-label text-white-50 mb-1">Impact level</label>
                <select
                  className="form-select form-select-sm"
                  value={form.impact_level}
                  onChange={(e) => setForm((p) => ({ ...p, impact_level: e.target.value }))}
                  disabled={isSaving}
                >
                  <option value="low">low</option>
                  <option value="medium">medium</option>
                  <option value="high">high</option>
                  <option value="very_high">very_high</option>
                </select>
              </div>

              <div className="col-12 col-md-4">
                <label className="form-label text-white-50 mb-1">Expected attendance</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  value={form.expected_attendance}
                  onChange={(e) => setForm((p) => ({ ...p, expected_attendance: e.target.value }))}
                  disabled={isSaving}
                  placeholder="e.g. 90000"
                />
              </div>

              <div className="col-12 col-md-4">
                <label className="form-label text-white-50 mb-1">Confidence</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control form-control-sm"
                  value={form.confidence}
                  onChange={(e) => setForm((p) => ({ ...p, confidence: e.target.value }))}
                  disabled={isSaving}
                  placeholder="0.00 - 1.00"
                />
              </div>

              <div className="col-12 col-md-4">
                <label className="form-label text-white-50 mb-1">Source</label>
                <input
                  className="form-control form-control-sm"
                  value={form.source}
                  onChange={(e) => setForm((p) => ({ ...p, source: e.target.value }))}
                  disabled={isSaving}
                  placeholder="e.g. official website"
                />
              </div>

              <div className="col-12">
                <label className="form-label text-white-50 mb-1">Notes</label>
                <textarea
                  className="form-control form-control-sm"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                  disabled={isSaving}
                  placeholder="Short description, expected demand, etc."
                />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-outline-light" onClick={onClose} disabled={isSaving}>
              Cancel
            </button>

            <button type="button" className="btn btn-info" onClick={onSave} disabled={isSaving}>
              {isSaving ? "Saving…" : "Save event"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ScraperTargetsManager() {

  const [searchParams] = useSearchParams();
  const businessUnitId = searchParams.get("business_unit_id");

  const [config, setConfig] = useState(null);
  const [loaded, setLoaded] = useState(false);

  // ---------------------------
  // Mapping UI state
  // ---------------------------
  const [roomCategories, setRoomCategories] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [mappings, setMappings] = useState([]);

  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [mapSaving, setMapSaving] = useState(false);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null); // {id,name}
  const [selectedKeys, setSelectedKeys] = useState([]); // ["hotel_code|room_type_norm"]
  const [modalFilter, setModalFilter] = useState("");

  // ---------------------------
  // City Events UI state
  // ---------------------------
  const [events, setEvents] = useState([]);
  const [eventsLoaded, setEventsLoaded] = useState(false);
  const [eventsError, setEventsError] = useState(null);

  const [showEventModal, setShowEventModal] = useState(false);
  const [activeEventId, setActiveEventId] = useState(null);
  const [eventSaving, setEventSaving] = useState(false);

  const [eventForm, setEventForm] = useState(() => ({ ...EMPTY_EVENT_FORM }));
  const isEditingEvent = activeEventId !== null;

  const reloadEvents = async (reason = "events_reload") => {
    setEventsLoaded(false);
    setEventsError(null);

    if (!businessUnitId) {
      console.warn("[CITY_EVENTS][GET] Missing business_unit_id in URL", { reason });
      setEvents([]);
      setEventsLoaded(true);
      return;
    }

    try {
      console.log("[CITY_EVENTS][GET] Loading events", {
        reason,
        business_unit_id: Number(businessUnitId),
      });

      const res = await axios.get("/api/city-events", {
        params: { business_unit_id: Number(businessUnitId) },
      });

      const rows = Array.isArray(res.data) ? res.data : [];
      setEvents(rows);

      console.log("[CITY_EVENTS][GET] Loaded", { rows: rows.length });
      setEventsLoaded(true);
    } catch (err) {
      console.error("[CITY_EVENTS][GET][ERROR]", err?.response?.data || err);
      setEvents([]);
      setEventsError(err?.response?.data?.error || err?.message || "LOAD_EVENTS_ERROR");
      setEventsLoaded(true);
    }
  };

  const openCreateEventModal = () => {
    console.log("[CITY_EVENTS][UI] openCreateEventModal()");
    setActiveEventId(null);
    setEventForm({ ...EMPTY_EVENT_FORM });
    setShowEventModal(true);
  };

  const openEditEventModal = (eventRow) => {
    const nextId = Number(eventRow?.id);
    if (!Number.isFinite(nextId)) return;

    console.log("[CITY_EVENTS][UI] openEditEventModal()", { id: nextId });
    setActiveEventId(nextId);
    setEventForm(buildEventFormFromRow(eventRow));
    setShowEventModal(true);
  };

  const resetEventModal = () => {
    setShowEventModal(false);
    setActiveEventId(null);
    setEventForm({ ...EMPTY_EVENT_FORM });
  };

  const closeEventModal = () => {
    if (eventSaving) return;
    resetEventModal();
  };

  const buildEventPayload = () => ({
    business_unit_id: Number(businessUnitId),
    city_code: String(eventForm.city_code || "").trim() || null,
    name: String(eventForm.name || "").trim(),
    start_date: eventForm.start_date || null,
    end_date: eventForm.end_date || null,
    impact_level: eventForm.impact_level || "medium",
    expected_attendance:
      eventForm.expected_attendance === "" ? null : Number(eventForm.expected_attendance),
    source: String(eventForm.source || "").trim() || null,
    confidence: eventForm.confidence === "" ? null : Number(eventForm.confidence),
    notes: String(eventForm.notes || "").trim() || null,
  });

  const createEvent = async () => {
    if (!businessUnitId) return;

    setEventSaving(true);
    setEventsError(null);

    try {
      const payload = buildEventPayload();

      console.log("[CITY_EVENTS][POST] Creating event", payload);

      await axios.post("/api/city-events", payload);

      console.log("[CITY_EVENTS][POST] Created OK");
      resetEventModal();
      await reloadEvents("after_create");
    } catch (err) {
      console.error("[CITY_EVENTS][POST][ERROR]", err?.response?.data || err);
      setEventsError(err?.response?.data?.error || err?.message || "CREATE_EVENT_ERROR");
    } finally {
      setEventSaving(false);
    }
  };

  const updateEvent = async () => {
    if (!businessUnitId || activeEventId === null) return;

    setEventSaving(true);
    setEventsError(null);

    try {
      const payload = buildEventPayload();
      console.log("[CITY_EVENTS][PUT] Updating event", { id: activeEventId, payload });

      try {
        await axios.put(`/api/city-events/${activeEventId}`, payload);
      } catch (putErr) {
        const status = putErr?.response?.status;
        if (status !== 404 && status !== 405) throw putErr;

        console.warn("[CITY_EVENTS][PUT] Falling back to PATCH", {
          id: activeEventId,
          status,
        });
        await axios.patch(`/api/city-events/${activeEventId}`, payload);
      }

      console.log("[CITY_EVENTS][PUT] Updated OK", { id: activeEventId });
      resetEventModal();
      await reloadEvents("after_update");
    } catch (err) {
      console.error("[CITY_EVENTS][PUT][ERROR]", err?.response?.data || err);
      setEventsError(err?.response?.data?.error || err?.message || "UPDATE_EVENT_ERROR");
    } finally {
      setEventSaving(false);
    }
  };

  const deleteEvent = async (eventRow) => {
    const eventId = Number(eventRow?.id);
    if (!Number.isFinite(eventId)) return;

    const eventName = String(eventRow?.name || "this event").trim() || "this event";
    if (!window.confirm(`Are you sure you want to delete "${eventName}"?`)) return;

    setEventSaving(true);
    setEventsError(null);

    try {
      console.log("[CITY_EVENTS][DELETE] Removing event", { id: eventId });
      await axios.delete(`/api/city-events/${eventId}`);

      console.log("[CITY_EVENTS][DELETE] Deleted OK", { id: eventId });
      if (activeEventId === eventId) resetEventModal();
      await reloadEvents("after_delete");
    } catch (err) {
      console.error("[CITY_EVENTS][DELETE][ERROR]", err?.response?.data || err);
      setEventsError(err?.response?.data?.error || err?.message || "DELETE_EVENT_ERROR");
    } finally {
      setEventSaving(false);
    }
  };

  const sourcesLabel = useMemo(() => {
    const meta = Array.isArray(config?.sources_meta) ? config.sources_meta : [];
    if (meta.length === 0) return "";
    return meta.map((s) => String(s?.source_name || "").trim()).filter(Boolean).join(", ");
  }, [config]);

  const competitors = useMemo(() => {
    const list = Array.isArray(config?.competitor_hotels) ? config.competitor_hotels : [];
    return list
      .map((h) => ({
        name: String(h?.name || "").trim(),
        address: String(h?.address || "").trim(),
      }))
      .filter((h) => !!h.name);
  }, [config]);

  const hotelNameByCode = useMemo(() => {
    const m = new Map();
    const list = Array.isArray(config?.competitor_hotels) ? config.competitor_hotels : [];
    for (const h of list) {
      const code = Number(h?.hotel_code);
      const name = String(h?.name || "").trim();
      if (code && name) m.set(code, name);
    }
    return m;
  }, [config]);


  const reloadScope = async (reason = "reload") => {
    if (!businessUnitId) {
      console.warn("[RATEMETRICS][SCOPE] Missing business_unit_id in URL", { reason });
      setConfig(null);
      setLoaded(true);
      return;
    }

    try {
      console.log("[RATEMETRICS][SCOPE] Loading config", { reason, businessUnitId });

      const res = await axios.get("/api/ratemetrics/rateshop-configs", {
        params: { business_unit_id: Number(businessUnitId) },
      });

      const rows = Array.isArray(res.data) ? res.data : [];
      const row = rows[0] || null;

      console.log("[RATEMETRICS][SCOPE] Loaded", {
        businessUnitId: Number(businessUnitId),
        found: !!row,
        competitors: Array.isArray(row?.competitor_hotels) ? row.competitor_hotels.length : 0,
        sources: Array.isArray(row?.sources_meta) ? row.sources_meta.length : 0,
      });

      setConfig(row);
      setLoaded(true);
    } catch (err) {
      console.error("[RATEMETRICS][SCOPE][ERROR] Failed to load config", err?.response?.data || err);
      setConfig(null);
      setLoaded(true);
    }
  };

  const reloadMappingData = async (reason = "reload_mapping") => {
    setMapLoaded(false);
    setMapError(null);

    try {
      if (!businessUnitId) {
        setRoomCategories([]);
        setRoomTypes([]);
        setMappings([]);
        setMapLoaded(true);
        return;
      }

      console.log("[RATEMETRICS][MAP] Loading mapping data", { reason, businessUnitId });

      const [catsRes, typesRes, mapsRes] = await Promise.all([
        axios.get("/api/room_categories", {
          params: { business_unit_id: Number(businessUnitId) },
        }),
        axios.get("/api/ratemetrics/room-types/distinct"),
        axios.get("/api/ratemetrics/competitor-room-type-mappings"),
      ]);

      setRoomCategories(Array.isArray(catsRes.data) ? catsRes.data : []);
      setRoomTypes(Array.isArray(typesRes.data) ? typesRes.data : []);
      setMappings(Array.isArray(mapsRes.data) ? mapsRes.data : []);

      console.log("[RATEMETRICS][MAP] Loaded", {
        categories: Array.isArray(catsRes.data) ? catsRes.data.length : 0,
        roomTypes: Array.isArray(typesRes.data) ? typesRes.data.length : 0,
        mappings: Array.isArray(mapsRes.data) ? mapsRes.data.length : 0,
      });

      setMapLoaded(true);
    } catch (err) {
      console.error("[RATEMETRICS][MAP][ERROR] Failed to load mapping data", err?.response?.data || err);
      setMapError(err?.response?.data?.error || err?.message || "LOAD_MAPPING_ERROR");
      setMapLoaded(true);
    }
  };

  useEffect(() => {
    setLoaded(false);
    reloadScope("mount_or_bu_change");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessUnitId]);

  // cuando hay config (y por tanto se renderiza), carga mapping data
    useEffect(() => {
    if (!loaded) return;
    if (!config) return;
    reloadMappingData("after_scope_loaded");
    reloadEvents("after_scope_loaded");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, config]);


  // ---------------------------
  // Helpers (mapping)
  // ---------------------------
  const makeKey = (hotel_code, room_type_norm) => `${Number(hotel_code)}|${String(room_type_norm || "").trim()}`;

  const roomTypesByKey = useMemo(() => {
    const m = new Map();
    for (const r of roomTypes) {
      const key = makeKey(r.hotel_code, r.room_type_norm);
      m.set(key, r);
    }
    return m;
  }, [roomTypes]);

  const mappingsByCategory = useMemo(() => {
    const out = new Map(); // catId -> array
    for (const mp of mappings) {
      const catId = Number(mp.room_category_id);
      if (!out.has(catId)) out.set(catId, []);
      out.get(catId).push(mp);
    }
    // ordenar estable
    for (const [catId, arr] of out.entries()) {
      arr.sort((a, b) => {
        const ak = makeKey(a.competitor_hotel_code, a.competitor_room_type_norm);
        const bk = makeKey(b.competitor_hotel_code, b.competitor_room_type_norm);
        return ak.localeCompare(bk);
      });
      out.set(catId, arr);
    }
    return out;
  }, [mappings]);

  const filteredModalRoomTypes = useMemo(() => {
    const f = modalFilter.trim().toLowerCase();
    if (!f) return roomTypes;

    return roomTypes.filter((r) => {
      const hotelName = String(hotelNameByCode.get(Number(r.hotel_code)) || "").toLowerCase();
      const rt = String(r.room_type || "").toLowerCase();
      const hc = String(r.hotel_code || "");
      return hotelName.includes(f) || rt.includes(f) || hc.includes(f);
    });
  }, [roomTypes, modalFilter, hotelNameByCode]);


  // ✅ Early returns SIEMPRE después de todos los hooks
  if (loaded && !config) return null;
  if (!loaded) return null;

  const openModalForCategory = (cat) => {
    const catId = Number(cat.id);
    const existing = mappingsByCategory.get(catId) || [];
    const keys = existing.map((m) => makeKey(m.competitor_hotel_code, m.competitor_room_type_norm));
    setActiveCategory({ id: catId, name: cat.name });
    setSelectedKeys(keys);
    setModalFilter("");
    setShowModal(true);
  };

  const toggleKey = (key) => {
    setSelectedKeys((prev) => {
      const s = new Set(prev);
      if (s.has(key)) s.delete(key);
      else s.add(key);
      return Array.from(s);
    });
  };

  const deleteMappingById = async (id) => {
    setMapError(null);
    try {
      await axios.delete(`/api/ratemetrics/competitor-room-type-mappings/${id}`);
      setMappings((prev) => prev.filter((m) => m.id !== id));
    } catch (err) {
      console.error("[RATEMETRICS][MAP][DELETE][ERROR]", err?.response?.data || err);
      setMapError(err?.response?.data?.error || err?.message || "DELETE_MAPPING_ERROR");
    }
  };

  const saveModalSelection = async () => {
    if (!activeCategory?.id) return;
    const catId = Number(activeCategory.id);

    setMapSaving(true);
    setMapError(null);

    try {
      const existing = mappingsByCategory.get(catId) || [];
      const existingKeys = new Set(
        existing.map((m) => makeKey(m.competitor_hotel_code, m.competitor_room_type_norm))
      );
      const desiredKeys = new Set(selectedKeys);

      const toDelete = existing.filter((m) => !desiredKeys.has(makeKey(m.competitor_hotel_code, m.competitor_room_type_norm)));
      const toAdd = selectedKeys.filter((k) => !existingKeys.has(k));

      console.log("[RATEMETRICS][MAP][SAVE] diff", {
        catId,
        toDelete: toDelete.length,
        toAdd: toAdd.length,
      });

      // deletions
      for (const m of toDelete) {
        await axios.delete(`/api/ratemetrics/competitor-room-type-mappings/${m.id}`);
      }

      // inserts
      for (const key of toAdd) {
        const rt = roomTypesByKey.get(key);
        if (!rt) continue;

        await axios.post("/api/ratemetrics/competitor-room-type-mappings", {
          competitor_hotel_code: Number(rt.hotel_code),
          competitor_hotel_name: hotelNameByCode.get(Number(rt.hotel_code)) || null,
          competitor_room_type_raw: String(rt.room_type || ""),
          competitor_room_type_norm: String(rt.room_type_norm || ""),
          room_category_id: catId,
        });
      }

      // refresh mappings
      const mapsRes = await axios.get("/api/ratemetrics/competitor-room-type-mappings");
      setMappings(Array.isArray(mapsRes.data) ? mapsRes.data : []);

      setShowModal(false);
      setActiveCategory(null);
      setSelectedKeys([]);
      setModalFilter("");
    } catch (err) {
      console.error("[RATEMETRICS][MAP][SAVE][ERROR]", err?.response?.data || err);
      setMapError(err?.response?.data?.error || err?.message || "SAVE_MAPPING_ERROR");
    } finally {
      setMapSaving(false);
    }
  };

    

    return (
      <>
      
        <div
          style={{
            maxHeight: "calc(100vh - 140px)",
            overflowY: "auto",
            overflowX: "hidden",
            paddingBottom: 16,
          }}
        >
        {/* =========================
            CARD 1: Scope (tu card actual)
          ========================= */}
        <div
          className="p-3 rounded"
          style={{
            background: "linear-gradient(135deg, rgba(26,188,156,0.18), rgba(52,152,219,0.10))",
            border: "1px solid rgba(26,188,156,0.25)",
            boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <div className="text-info fw-bold" style={{ fontSize: "1.05rem" }}>
                Market Intelligence Scope
              </div>
              <div className="text-white-50" style={{ fontSize: "0.85rem" }}>
                This configuration defines which market data will be collected for this business unit.
              </div>
            </div>

            <div
              className="px-3 py-2 rounded"
              style={{
                background: "rgba(0,0,0,0.35)",
                border: "1px solid rgba(255,255,255,0.08)",
                fontSize: "0.85rem",
              }}
            >
              <span className="text-white-50">Sources:</span>{" "}
              <span className="text-white">{sourcesLabel || "–"}</span>
            </div>
          </div>

          <div className="row g-3 mt-1">
            {/* Left: Parameters */}
            <div className="col-12 col-lg-5">
              <div
                className="p-3 rounded"
                style={{
                  background: "rgba(0,0,0,0.30)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <div className="text-white fw-bold mb-2" style={{ fontSize: "0.95rem" }}>
                  Parameters
                </div>

                <div className="d-flex justify-content-between py-1" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                  <span className="text-white-50">Currency</span>
                  <span className="text-white">{config.currency || "–"}</span>
                </div>

                <div className="d-flex justify-content-between py-1" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                  <span className="text-white-50">Horizon</span>
                  <span className="text-white">{config.horizon ? `${config.horizon} days` : "–"}</span>
                </div>

                <div className="d-flex justify-content-between py-1" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                  <span className="text-white-50">Occupancy</span>
                  <span className="text-white">{config.occupancy ?? "–"}</span>
                </div>

                <div className="d-flex justify-content-between py-1">
                  <span className="text-white-50">Length of Stay</span>
                  <span className="text-white">{config.los ? `${config.los} nights` : "–"}</span>
                </div>
              </div>
            </div>

            {/* Right: Competitors */}
            <div className="col-12 col-lg-7">
              <div
                className="p-3 rounded"
                style={{
                  background: "rgba(0,0,0,0.30)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <div className="text-white fw-bold mb-2" style={{ fontSize: "0.95rem" }}>
                  Competitor Hotels
                </div>

                {competitors.length === 0 ? (
                  <div className="text-white-50" style={{ fontSize: "0.9rem" }}>
                    No competitors configured.
                  </div>
                ) : (
                  <div className="table-responsive" style={{ overflowX: "auto" }}>
                    <table
                      className="table table-dark table-striped table-sm align-middle mb-0"
                      style={{
                        fontSize: "0.78rem",
                        lineHeight: "1.0rem",
                        tableLayout: "auto",
                        width: "100%",
                        minWidth: "700px",
                        whiteSpace: "nowrap",
                      }}

                    >
                      <thead>
                        <tr>
                          <th style={{ minWidth: 260, whiteSpace: "normal" }}>Hotel</th>
                          <th style={{ minWidth: 360, whiteSpace: "normal" }}>Address</th>
                        </tr>
                      </thead>
                      <tbody>
                        {competitors.map((h, idx) => (
                          <tr key={`comp-${idx}`}>
                            <td style={{ whiteSpace: "normal" }}>
                              <span className="text-white fw-bold">{h.name}</span>
                            </td>
                            <td style={{ whiteSpace: "normal" }}>
                              <span className="text-white-50">{h.address || "–"}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="text-white-50 mt-2" style={{ fontSize: "0.8rem" }}>
                  Only the hotels and parameters listed here will be queried for this business unit.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* =========================
            CARD 2: Mapping (NUEVA, debajo)
          ========================= */}
        <div
          className="p-3 rounded mt-3"
          style={{
            background: "linear-gradient(135deg, rgba(155,89,182,0.16), rgba(52,152,219,0.10))",
            border: "1px solid rgba(155,89,182,0.22)",
            boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <div className="text-info fw-bold" style={{ fontSize: "1.05rem" }}>
                Competitor Room Types Assignment
              </div>
              <div className="text-white-50" style={{ fontSize: "0.85rem" }}>
                Pick competitor room types per Room Category (multi-select). This will be used later to apply prices.
              </div>
            </div>

            <button
              className="btn btn-sm btn-outline-info"
              type="button"
              onClick={() => reloadMappingData("manual_refresh")}
              disabled={mapSaving}
            >
              Refresh
            </button>
          </div>

          {!mapLoaded ? null : (
            <>
              {mapError && (
                <div className="alert alert-danger py-2 mb-2">
                  {String(mapError)}
                </div>
              )}

              <div className="table-responsive" style={{ overflowX: "auto" }}>
              <table
                className="table table-dark table-striped table-sm align-middle mb-0"
                style={{
                  fontSize: "0.78rem",
                  lineHeight: "1.0rem",
                  tableLayout: "auto",
                  width: "100%",
                  minWidth: "700px",
                  whiteSpace: "nowrap",
                }}

              >
                <thead>
                  <tr>
                    <th style={{ minWidth: 260, whiteSpace: "normal" }}>Room Category</th>
                    <th style={{ minWidth: 360, whiteSpace: "normal" }}>Assigned competitor room types</th>
                    <th style={{ width: 180 }}>Actions</th>
                  </tr>
                </thead>


                  <tbody>
                    {roomCategories.map((cat) => {
                      const catId = Number(cat.id);
                      const assigned = mappingsByCategory.get(catId) || [];

                      return (
                        <tr key={`cat-${catId}`}>
                          <td style={{ whiteSpace: "normal", overflowWrap: "anywhere", wordBreak: "break-word" }}>
                            <div className="fw-bold">{cat.name}</div>
                          </td>


                          <td style={{ whiteSpace: "normal" }}>
                            {assigned.length === 0 ? (
                              <span className="text-white-50">No room types assigned.</span>
                            ) : (
                              <div className="d-flex flex-wrap gap-2">
                                {assigned.map((m) => {
                                  const key = makeKey(m.competitor_hotel_code, m.competitor_room_type_norm);
                                  const rt = roomTypesByKey.get(key);
                                  const hName = hotelNameByCode.get(Number(m.competitor_hotel_code)) || m.competitor_hotel_name || m.competitor_hotel_code;
                                  const label = rt
                                    ? `${rt.room_type} (${hName})`
                                    : `${m.competitor_room_type_raw} (${hName})`;


                                  return (
                                    <span
                                      key={`chip-${m.id}`}
                                      className="badge rounded-pill bg-secondary"
                                      style={{
                                        fontSize: "0.78rem",
                                        padding: "6px 10px",
                                        maxWidth: "100%",
                                        whiteSpace: "normal",
                                        overflowWrap: "anywhere",
                                        textAlign: "left",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 6,
                                      }}

                                      title={key}
                                    >
                                      {label}
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-link text-white ms-2 p-0"
                                        style={{ textDecoration: "none", lineHeight: 1 }}
                                        onClick={() => deleteMappingById(m.id)}
                                        disabled={mapSaving}
                                        title="Remove"
                                      >
                                        ×
                                      </button>
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </td>

                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-light"
                              onClick={() => openModalForCategory(cat)}
                              disabled={mapSaving}
                            >
                              Select room types
                            </button>
                          </td>
                        </tr>
                      );
                    })}

                    {roomCategories.length === 0 && (
                      <tr>
                        <td colSpan={3} className="text-white-50">
                          No room categories found for this business unit.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="text-white-50 mt-2" style={{ fontSize: "0.8rem" }}>
                You can assign multiple competitor room types to the same category. These mappings are persisted in DB.
              </div>
            </>
          )}
          </div>

        {/* =========================
            CARD 3: City Events (NUEVA, debajo)
          ========================= */}
        <div
          className="p-3 rounded mt-3"
          style={{
            background: "linear-gradient(135deg, rgba(241,196,15,0.14), rgba(52,152,219,0.08))",
            border: "1px solid rgba(241,196,15,0.20)",
            boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <div className="text-info fw-bold" style={{ fontSize: "1.05rem" }}>
                City Events
              </div>
              <div className="text-white-50" style={{ fontSize: "0.85rem" }}>
                Create and store demand-driving events for this business unit. These events will be used by pricing logic later.
              </div>
            </div>

            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-outline-info"
                type="button"
                onClick={() => reloadEvents("manual_refresh")}
                disabled={eventSaving}
              >
                Refresh
              </button>

            <button
              className="btn btn-sm btn-info"
              type="button"
              onClick={() => {
                console.log("[CITY_EVENTS][UI] Click Add event", { eventSaving });
                openCreateEventModal();
              }}
              disabled={eventSaving}
            >
              Add event
            </button>

            </div>
          </div>

          {!eventsLoaded ? null : (
            <>
              {eventsError && (
                <div className="alert alert-danger py-2 mb-2">
                  {String(eventsError)}
                </div>
              )}

              <div
                className="table-responsive"
                style={{
                  maxHeight: "500px",
                  overflowY: "auto",
                  overflowX: "auto",
                }}
              >
                <table
                  className="table table-dark table-striped table-sm align-middle mb-0"
                  style={{
                    fontSize: "0.78rem",
                    lineHeight: "1.0rem",
                    tableLayout: "auto",
                    width: "auto",
                    minWidth: "1100px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <thead>
                    <tr>
                      <th style={{ minWidth: 260, whiteSpace: "normal" }}>Event</th>
                      <th style={{ width: 120 }}>City</th>
                      <th style={{ width: 120 }}>Start</th>
                      <th style={{ width: 120 }}>End</th>
                      <th style={{ width: 120 }}>Impact</th>
                      <th style={{ width: 150, textAlign: "right" }}>Attendance</th>
                      <th style={{ minWidth: 220, whiteSpace: "normal" }}>Source</th>
                      <th style={{ width: 120, textAlign: "right" }}>Confidence</th>
                      <th style={{ width: 180 }}>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {events.map((e) => (
                      <tr key={`evt-${e.id}`}>
                        <td style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>
                          <div className="fw-bold">{e.name || "–"}</div>
                          {e.notes ? (
                            <div className="text-white-50" style={{ fontSize: "0.76rem" }}>
                              {String(e.notes).slice(0, 160)}
                              {String(e.notes).length > 160 ? "…" : ""}
                            </div>
                          ) : null}
                        </td>

                        <td className="text-white-50">{e.city_code || "–"}</td>
                        <td className="text-white">{e.start_date || "–"}</td>
                        <td className="text-white">{e.end_date || "–"}</td>
                        <td className="text-white">{e.impact_level || "–"}</td>

                        <td className="text-white" style={{ textAlign: "right" }}>
                          {e.expected_attendance ?? "–"}
                        </td>

                        <td style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>
                          <span className="text-white-50">{e.source || "–"}</span>
                        </td>

                        <td className="text-white" style={{ textAlign: "right" }}>
                          {e.confidence ?? "–"}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-light"
                              onClick={() => openEditEventModal(e)}
                              disabled={eventSaving || !Number.isFinite(Number(e?.id))}
                            >
                              Edit
                            </button>

                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => deleteEvent(e)}
                              disabled={eventSaving || !Number.isFinite(Number(e?.id))}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}

                    {events.length === 0 && (
                      <tr>
                        <td colSpan={9} className="text-white-50">
                          No events found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        </div>

      {/* =========================
          MODAL (multi-select + chips)
        ========================= */}

      {showModal && (
        <div
          className="modal d-block"
          tabIndex="-1"
          role="dialog"
          style={{ background: "rgba(0,0,0,0.65)" }}
        >
          <div className="modal-dialog modal-xl" role="document" style={{ maxWidth: "1400px" }}>
            <div
              className="modal-content bg-dark text-light"
              style={{ border: "1px solid rgba(255,255,255,0.10)" }}
            >
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-0">Select competitor room types</h5>
                  <div className="text-white-50" style={{ fontSize: "0.85rem" }}>
                    Room Category:{" "}
                    <span className="text-white">{activeCategory?.name || "–"}</span>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn btn-sm btn-outline-light"
                  onClick={() => {
                    if (mapSaving) return;
                    setShowModal(false);
                    setActiveCategory(null);
                    setSelectedKeys([]);
                    setModalFilter("");
                  }}
                  disabled={mapSaving}
                >
                  Close
                </button>

              </div>

              <div className="modal-body">
                {mapError && (
                  <div className="alert alert-danger py-2 mb-2">
                    {String(mapError)}
                  </div>
                )}

                {/* Chips */}
                <div className="mb-2">
                  <div className="text-white-50 mb-1" style={{ fontSize: "0.85rem" }}>
                    Selected ({selectedKeys.length})
                  </div>

                  {selectedKeys.length === 0 ? (
                    <div className="text-white-50">No room types selected.</div>
                  ) : (
                    <div className="d-flex flex-wrap gap-2">
                      {selectedKeys.map((k) => {
                        const rt = roomTypesByKey.get(k);
                        const label = rt
                          ? `${rt.room_type} (${hotelNameByCode.get(Number(rt.hotel_code)) || rt.hotel_code})`
                          : k;

                        return (
                          <span
                            key={`sel-${k}`}
                            className="badge rounded-pill bg-secondary"
                            style={{ padding: "6px 10px", whiteSpace: "normal" }}
                          >
                            {label}
                            <button
                              type="button"
                              className="btn btn-sm btn-link text-white ms-2 p-0"
                              style={{ textDecoration: "none", lineHeight: 1 }}
                              onClick={() => toggleKey(k)}
                              disabled={mapSaving}
                              title="Unselect"
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Filter */}
                <input
                  className="form-control form-control-sm mb-2"
                  placeholder="Filter by hotel name / room type / hotel code…"
                  value={modalFilter}
                  onChange={(e) => setModalFilter(e.target.value)}
                  disabled={mapSaving}
                />

                {/* List */}
                <div className="table-responsive" style={{ maxHeight: 520, overflowY: "auto", overflowX: "auto" }}>
                  <table
                    className="table table-dark table-hover table-sm align-middle mb-0"
                    style={{
                      fontSize: "0.78rem",
                      lineHeight: "1.0rem",
                      tableLayout: "auto",
                      width: "auto",
                      minWidth: "1100px",
                    }}
                  >
                    <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
                      <tr>
                        <th style={{ width: 46 }}></th>
                        <th style={{ minWidth: 260, whiteSpace: "normal" }}>Room type</th>
                        <th style={{ minWidth: 360, whiteSpace: "normal" }}>Room description</th>
                        <th style={{ minWidth: 320, whiteSpace: "normal" }}>Amenities</th>
                        <th style={{ minWidth: 220, whiteSpace: "normal" }}>Hotel</th>
                      </tr>
                    </thead>

                    <tbody>
                      {filteredModalRoomTypes.map((rt) => {
                        const key = makeKey(rt.hotel_code, rt.room_type_norm);
                        const checked = selectedKeys.includes(key);

                        const roomDesc =
                          rt.room_description ?? rt.roomDescription ?? null;

                        const roomAmenities =
                          rt.room_amenities ?? rt.roomAmenities ?? null;

                        return (
                          <tr key={`rt-${key}`}>
                            <td>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={checked}
                                onChange={() => toggleKey(key)}
                                disabled={mapSaving}
                              />
                            </td>

                            <td style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>
                              <div className="fw-bold">{rt.room_type}</div>
                            </td>

                            <td style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>
                              <span className="text-white-50">{roomDesc || "–"}</span>
                            </td>

                            <td style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>
                              <span className="text-white-50">{roomAmenities || "–"}</span>
                            </td>

                            <td style={{ whiteSpace: "normal" }}>
                              <div>{hotelNameByCode.get(Number(rt.hotel_code)) || "–"}</div>
                            </td>
                          </tr>
                        );
                      })}

                      {filteredModalRoomTypes.length === 0 && (
                        <tr>
                          <td colSpan={5} className="text-white-50">
                            No room types found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-light"
                  onClick={() => {
                    if (mapSaving) return;
                    setShowModal(false);
                    setActiveCategory(null);
                    setSelectedKeys([]);
                    setModalFilter("");
                  }}
                  disabled={mapSaving}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="btn btn-info"
                  onClick={saveModalSelection}
                  disabled={mapSaving}
                >
                  {mapSaving ? "Saving…" : "Save selection"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =========================
          CITY EVENTS MODAL (Create / Edit)
          Uses existing Pilcore overlay classes (no new CSS)
        ========================= */}
      {showEventModal && (
        <CityEventModal
          isOpen={showEventModal}
          isSaving={eventSaving}
          isEditing={isEditingEvent}
          form={eventForm}
          setForm={setEventForm}
          onClose={closeEventModal}
          onSave={isEditingEvent ? updateEvent : createEvent}
        />
      )}



      </>
    );

}
